"use client";

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { LiveShowCard } from "@/components/live-show-card";

interface CallInShowCarouselItem {
  title: string;
  subtitle: string;
  description: string;
  tags: string[];
  ctaHref: string;
}

interface CallInShowCarouselProps {
  items: CallInShowCarouselItem[];
  className?: string;
}

export function CallInShowCarousel({ items, className }: CallInShowCarouselProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (items.length <= 1 || isPaused) return;

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % items.length);
    }, 4200);

    return () => window.clearInterval(timer);
  }, [isPaused, items.length]);

  const activeItem = items[activeIndex];

  function showPrevious() {
    setActiveIndex((current) => (current - 1 + items.length) % items.length);
  }

  function showNext() {
    setActiveIndex((current) => (current + 1) % items.length);
  }

  if (!activeItem) return null;

  return (
    <div
      className={className}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <LiveShowCard
        title={activeItem.title}
        subtitle={activeItem.subtitle}
        description={activeItem.description}
        tags={activeItem.tags}
        statusLabel="Call-in Show"
        ctaHref={activeItem.ctaHref}
        ctaLabel="Zur Show"
      />
      {items.length > 1 && (
        <div className="mt-3 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={showPrevious}
            aria-label="Vorherige Show"
            className="flex size-7 items-center justify-center rounded-full border border-border/70 text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
          >
            <ChevronLeft className="size-4" />
          </button>
          {items.map((item, index) => (
            <button
              key={item.title}
              type="button"
              onClick={() => setActiveIndex(index)}
              aria-label={`Slide ${index + 1}: ${item.title}`}
              className={[
                "h-2.5 rounded-full transition-all",
                index === activeIndex ? "w-8 bg-primary" : "w-2.5 bg-border hover:bg-primary/50",
              ].join(" ")}
            />
          ))}
          <button
            type="button"
            onClick={showNext}
            aria-label="Nächste Show"
            className="flex size-7 items-center justify-center rounded-full border border-border/70 text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      )}
    </div>
  );
}
